import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../../services/api';
import ArborescenceLogo from '../../../components/ArborescenceLogo';
import { ArrowLeft, Save, AlertCircle } from 'lucide-react';
import '../../../styles/admin/ERPConfig.css';


const ERPConfig = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await api.get('/api/erp/config', {
      });
      setConfig(response.data.config);
    } catch (error) {
      if (error.response?.status === 401) {
        
        navigate('/auth/login');
      }
      setMessage({ type: 'error', text: 'Erreur lors du chargement' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await api.put('/api/erp/config', config, {
      });
      setMessage({ type: 'success', text: 'Configuration enregistrée avec succès!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors de la sauvegarde' });
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const updateEfficiencyScale = (employeeCount, value) => {
    setConfig(prev => ({
      ...prev,
      echelle_efficacite_employes: {
        ...prev.echelle_efficacite_employes,
        [employeeCount]: parseFloat(value)
      }
    }));
  };

  if (loading) {
    return <div className="admin-loading">Chargement...</div>;
  }

  return (
    <div className="erp-config-page">
      <div className="erp-config-header">
        <div className="erp-config-header-content">
          <Link to="/admin/erp" className="erp-back-link">
            <ArrowLeft size={20} />
            Retour au dashboard ERP
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '1rem' }}>
            <ArborescenceLogo size="small" showText={false} />
            <div>
              <h1>Configuration Système</h1>
              <p>Paramètres globaux d'Arborescence</p>
            </div>
          </div>
        </div>
      </div>

      <div className="erp-config-content">
        {message.text && (
          <div className={`erp-message ${message.type}`}>
            <AlertCircle size={20} />
            {message.text}
          </div>
        )}

        {/* Pricing Configuration */}
        <div className="config-section">
          <h2>💰 Tarification</h2>
          
          <div className="config-row">
            <label>
              Taux horaire par employé
              <span className="label-hint">Coût de base par employé par heure</span>
            </label>
            <div className="input-with-unit">
              <input
                type="number"
                step="0.01"
                value={config.taux_horaire_employe}
                onChange={(e) => updateConfig('taux_horaire_employe', parseFloat(e.target.value))}
              />
              <span className="input-unit">$ / heure</span>
            </div>
          </div>

          <div className="config-row">
            <label>
              Marge de profit
              <span className="label-hint">Pourcentage de profit sur coût total</span>
            </label>
            <div className="input-with-unit">
              <input
                type="number"
                step="0.1"
                value={config.marge_profit_pourcent}
                onChange={(e) => updateConfig('marge_profit_pourcent', parseFloat(e.target.value))}
              />
              <span className="input-unit">%</span>
            </div>
          </div>

          <div className="config-calculation-example">
            <strong>Exemple de calcul:</strong>
            <div>Coût total: 100$ → Prix client: {(100 / (1 - config.marge_profit_pourcent/100)).toFixed(2)}$ (marge de {config.marge_profit_pourcent}%)</div>
          </div>
        </div>

        {/* Labor Efficiency Scale */}
        <div className="config-section">
          <h2>👥 Échelle d'Efficacité Multi-Employés</h2>
          <p className="section-description">
            Définit l'efficacité réelle lorsque plusieurs employés travaillent ensemble.
            Par exemple, 2 employés ne travaillent pas 2x plus vite (coordination, espace, etc.)
          </p>

          <div className="efficiency-grid">
            {[1, 2, 3, 4, 5].map(count => (
              <div key={count} className="efficiency-row">
                <div className="efficiency-label">
                  <strong>{count}</strong> employé{count > 1 ? 's' : ''}
                </div>
                <div className="input-with-unit">
                  <input
                    type="number"
                    step="0.1"
                    value={config.echelle_efficacite_employes[count.toString()]}
                    onChange={(e) => updateEfficiencyScale(count.toString(), e.target.value)}
                  />
                  <span className="input-unit">x plus rapide</span>
                </div>
                <div className="efficiency-percentage">
                  ({(config.echelle_efficacite_employes[count.toString()] * 100).toFixed(0)}% d'efficacité totale)
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Taxes */}
        <div className="config-section">
          <h2>📊 Taxes</h2>
          
          <div className="config-row">
            <label>TPS (Taxe fédérale)</label>
            <div className="input-with-unit">
              <input
                type="number"
                step="0.001"
                value={config.taux_tps}
                onChange={(e) => updateConfig('taux_tps', parseFloat(e.target.value))}
              />
              <span className="input-unit">%</span>
            </div>
          </div>

          <div className="config-row">
            <label>TVQ (Taxe provinciale)</label>
            <div className="input-with-unit">
              <input
                type="number"
                step="0.001"
                value={config.taux_tvq}
                onChange={(e) => updateConfig('taux_tvq', parseFloat(e.target.value))}
              />
              <span className="input-unit">%</span>
            </div>
          </div>
        </div>

        {/* Invoicing */}
        <div className="config-section">
          <h2>🧾 Facturation</h2>
          
          <div className="config-row">
            <label>
              Intérêts de retard
              <span className="label-hint">Pourcentage par mois sur les paiements en retard</span>
            </label>
            <div className="input-with-unit">
              <input
                type="number"
                step="0.1"
                value={config.taux_interet_retard}
                onChange={(e) => updateConfig('taux_interet_retard', parseFloat(e.target.value))}
              />
              <span className="input-unit">% / mois</span>
            </div>
          </div>

          <div className="config-row">
            <label>
              Délai de paiement
              <span className="label-hint">Nombre de jours avant échéance</span>
            </label>
            <div className="input-with-unit">
              <input
                type="number"
                value={config.jours_avant_echeance}
                onChange={(e) => updateConfig('jours_avant_echeance', parseInt(e.target.value))}
              />
              <span className="input-unit">jours</span>
            </div>
          </div>
        </div>

        {/* PDF Template */}
        <div className="config-section">
          <h2>📄 Template PDF</h2>
          <p className="section-description">
            Option future: Importer un template PDF personnalisé pour les soumissions et factures
          </p>
          
          <div className="config-row">
            <label>Utiliser un template personnalisé</label>
            <input
              type="checkbox"
              checked={config.utiliser_template_custom}
              onChange={(e) => updateConfig('utiliser_template_custom', e.target.checked)}
              disabled
            />
            <span className="label-hint">(Fonctionnalité à venir)</span>
          </div>

          <div className="config-row">
            <label>URL du template</label>
            <input
              type="text"
              value={config.pdf_template_url || ''}
              onChange={(e) => updateConfig('pdf_template_url', e.target.value)}
              placeholder="https://..."
              disabled
            />
          </div>
        </div>

        {/* Save Button */}
        <div className="config-actions">
          <button 
            className="erp-save-btn"
            onClick={handleSave}
            disabled={saving}
          >
            <Save size={20} />
            {saving ? 'Enregistrement...' : 'Enregistrer la configuration'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ERPConfig;
